/**
 * OpenStreetMap tile stitcher.
 *
 * Builds a genuinely HIGH-RESOLUTION map image by fetching native OSM raster
 * tiles for a geographic window and drawing them onto a single large canvas.
 * This replaces snapshotting the small on-screen Leaflet map with html2canvas
 * (which only upscales the device viewport, so it can never exceed screen
 * resolution). Here the output resolution is bounded only by the tile zoom
 * level, so fine detail (street names, building outlines) stays legible when
 * the final photo is pinch-zoomed on a device.
 *
 * OSM tile servers send `Access-Control-Allow-Origin: *`, so tiles loaded with
 * crossOrigin='anonymous' can be drawn to a canvas and exported WITHOUT
 * tainting it (unlike a tainted canvas, which would throw on toBlob).
 */

const TILE_SIZE = 256;
const SUBDOMAINS = ['a', 'b', 'c'];

export interface StitchParams {
  /** Tile zoom level to render at (higher = more real detail). */
  zoom: number;
  /** World-pixel X of the window's left edge at `zoom`. */
  left: number;
  /** World-pixel Y of the window's top edge at `zoom`. */
  top: number;
  /** Output width in pixels. */
  width: number;
  /** Output height in pixels. */
  height: number;
  /** JPEG quality 0..1 (default 0.95). */
  quality?: number;
}

export interface StitchResult {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

function loadTile(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Required so the tiles don't taint the canvas (OSM serves CORS headers).
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Tile load failed: ${url}`));
    img.src = url;
  });
}

/**
 * Stitch the OSM tiles covering [left, top, width, height] (world pixels at
 * `zoom`) into one canvas and encode it as a JPEG.
 *
 * Throws if more than half the tiles fail to load, so the caller can fall back
 * to another capture method rather than ship a mostly-blank map.
 */
export async function stitchOsmTiles(params: StitchParams): Promise<StitchResult> {
  const { zoom, left, top, width, height, quality = 0.95 } = params;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // Neutral backdrop in case an edge tile is missing.
  ctx.fillStyle = '#f0f0f0';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const tilesPerAxis = Math.pow(2, zoom);
  const tx0 = Math.floor(left / TILE_SIZE);
  const ty0 = Math.floor(top / TILE_SIZE);
  const tx1 = Math.floor((left + width - 1) / TILE_SIZE);
  const ty1 = Math.floor((top + height - 1) / TILE_SIZE);

  const jobs: Promise<void>[] = [];
  let subIndex = 0;
  let failures = 0;
  let total = 0;

  for (let tx = tx0; tx <= tx1; tx++) {
    for (let ty = ty0; ty <= ty1; ty++) {
      // No vertical wrap (outside the world is just blank).
      if (ty < 0 || ty >= tilesPerAxis) continue;
      // Horizontal wrap so a window crossing the antimeridian still resolves.
      const xWrapped = ((tx % tilesPerAxis) + tilesPerAxis) % tilesPerAxis;
      const subdomain = SUBDOMAINS[subIndex++ % SUBDOMAINS.length];
      const url = `https://${subdomain}.tile.openstreetmap.org/${zoom}/${xWrapped}/${ty}.png`;
      const dx = tx * TILE_SIZE - left;
      const dy = ty * TILE_SIZE - top;
      total++;
      jobs.push(
        loadTile(url)
          .then((img) => {
            ctx.drawImage(img, dx, dy);
          })
          .catch(() => {
            failures++;
          }),
      );
    }
  }

  await Promise.all(jobs);

  if (total > 0 && failures / total > 0.5) {
    throw new Error(`Tile stitch failed: ${failures}/${total} tiles missing`);
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Failed to encode stitched map'))),
      'image/jpeg',
      quality,
    );
  });

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read stitched blob'));
    reader.readAsDataURL(blob);
  });

  return { blob, dataUrl, width: canvas.width, height: canvas.height };
}
